'use client'

import React, { createContext, useContext, useState } from 'react'
import { useDeliveryAddress } from '@/delivery-address/client'
import type { HelpCenterState, OrderId } from '@/help-center/entities'
import { getHelpCenterChatState, getHelpCenterUrl } from '@/help-center/use-cases'
import { useUserData } from '@/user/useUserData'

export interface HelpCenterContextType {
  isHelpCenterVisible: HelpCenterState['isHelpCenterVisible']
  helpCenterIframeUrl: HelpCenterState['helpCenterIframeUrl']
  chat: HelpCenterState['chat']
  fetchHelpCenterIframeUrl: () => Promise<string>
  fetchChatState: () => Promise<void>
  openHelpCenter: (orderId?: OrderId) => void
  closeHelpCenter: () => void
}

export const HelpCenterContext = createContext<HelpCenterContextType>({
  helpCenterIframeUrl: null,
  isHelpCenterVisible: false,
  chat: {
    unreadMessageCount: 0,
    active: false,
  },
  fetchHelpCenterIframeUrl: async () => '',
  fetchChatState: async () => undefined,
  openHelpCenter: () => undefined,
  closeHelpCenter: () => undefined,
})

export const HelpCenterProvider = ({ children }: { children: React.ReactNode }) => {
  const { userData } = useUserData()
  const userId = userData?.id

  const { deliveryAddress } = useDeliveryAddress()
  const countryCode = deliveryAddress?.countryCode

  const [helpCenterState, setHelpCenterState] = useState<HelpCenterState>({
    helpCenterIframeUrl: null,
    isHelpCenterVisible: false,
    chat: {
      unreadMessageCount: 0,
      active: false,
    },
    orderId: null,
  })

  const { helpCenterIframeUrl, isHelpCenterVisible, chat } = helpCenterState

  const fetchHelpCenterIframeUrl = async () => {
    const helpCenterIframeUrlResponse = await getHelpCenterUrl({
      orderId: helpCenterState.orderId ?? undefined,
      userId,
      countryCode,
    })
    setHelpCenterState({
      ...helpCenterState,
      helpCenterIframeUrl: helpCenterIframeUrlResponse.data.url,
    })
    return helpCenterIframeUrlResponse.data.url
  }

  const fetchChatState = async () => {
    const chatStateResponse = await getHelpCenterChatState()
    setHelpCenterState({
      ...helpCenterState,
      chat: {
        unreadMessageCount: chatStateResponse.data.chat.unread_message_count,
        active: chatStateResponse.data.chat.active,
      },
    })
  }

  const openHelpCenter = async (orderId?: OrderId) => {
    setHelpCenterState({
      ...helpCenterState,
      isHelpCenterVisible: true,
      orderId: orderId ?? null,
    })
  }

  const closeHelpCenter = () => {
    setHelpCenterState({
      ...helpCenterState,
      helpCenterIframeUrl: null,
      isHelpCenterVisible: false,
      orderId: null,
    })
  }

  return (
    <HelpCenterContext.Provider
      value={{
        helpCenterIframeUrl,
        isHelpCenterVisible,
        chat,
        fetchHelpCenterIframeUrl,
        fetchChatState,
        openHelpCenter,
        closeHelpCenter,
      }}
    >
      {children}
    </HelpCenterContext.Provider>
  )
}

export const useHelpCenter = () => useContext(HelpCenterContext)
